import { Select } from "@bafe/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { api } from "../../lib/api.js";
import { queryKeys } from "../../lib/query.js";
import { HubManualLookupFieldSelect } from "./hub-manual-lookup-field-select.js";
import { compareLocale, defaultColumnVisibility } from "./hub-operation-list-table.js";
import {
	HubOperationBar,
	HubOperationColumnMenu,
	HubOperationFilterClear,
	HubOperationFilterMenu,
	HubOperationEmpty,
	HubOperationGroupHeader,
	HubOperationListTable,
	HubOperationTableEmptyRow,
	HubOperationTablePagination,
	hubOperationGroupedHasRows,
	HubOperationFormField,
	HubOperationFormRow,
	HubOperationFormStack,
	HubOperationModal,
	HubOperationScreen,
	HubOperationSearchField,
	HubOperationSearchToggle,
	HubOperationToolbar,
	hubTableSortIndicator,
	toggleHubSort,
	useHubOperationSearchFocus,
} from "./hub-operation-shell.js";
import { useHubOperationPagination } from "./hub-operation-pagination.js";
import {
	DEFAULT_CONSUMABLE_CATEGORIES,
	DEFAULT_CONSUMABLE_UNITS,
	formatQty,
	mergeUniqueLists,
	nextConsumableCode,
	parseQty,
	type ConsumableSettings,
} from "./hub-consumables-logic.js";
import {
	applyConsumableMovement,
	consumableHasCustody,
	balanceQtyAt,
	migrateConsumableBalances,
	normalizeConsumableLocation,
	normalizeMovementType,
	type ConsumableStockLocation,
} from "./hub-consumables-stock.js";
import type { ConsumableBalanceRow, ConsumableMovementRow, ConsumableRow, ConsumableSettingsRow, OrgSiteBooks } from "./hub-operation-types.js";
import {
	customerLabel,
	customersOf,
	formatMyr,
	siteRow,
	sitesForCustomer,
	textOf,
	useHubSave,
	vendorLabel,
	vendorsOf,
} from "./hub-operation-shared.js";

export type ConsumablesBooks = OrgSiteBooks & {
	consumables: ConsumableRow[];
	consumableBalances?: ConsumableBalanceRow[];
	consumableMovements: ConsumableMovementRow[];
	consumableSettings?: ConsumableSettingsRow[];
};

function stockLocationLabel(books: ConsumablesBooks, loc: ConsumableStockLocation): string {
	if (loc.custody === "internal") return "On Hands";
	const customer = customerLabel(books, loc.customerId);
	const site = siteRow(books, loc.siteId)?.name ?? "";
	return [customer, site].filter(Boolean).join(" · ") || "With Client";
}

type ConsumableColumnId = "code" | "category" | "name" | "qty" | "minQty" | "location" | "unitPrice" | "brand";
const CONSUMABLE_TABLE_COLUMNS: Array<{ id: ConsumableColumnId; label: string; sortable?: boolean }> = [
	{ id: "code", label: "ID", sortable: true },
	{ id: "category", label: "Category", sortable: true },
	{ id: "name", label: "Item", sortable: true },
	{ id: "brand", label: "Brand", sortable: true },
	{ id: "qty", label: "Qty", sortable: true },
	{ id: "minQty", label: "Min", sortable: true },
	{ id: "location", label: "Custody", sortable: true },
	{ id: "unitPrice", label: "Unit price", sortable: true },
];

type ConsumableView = "items" | "history";
type ConsumableStockFilter = "" | "internal" | "external";
type ConsumableGroupBy = "category" | "";

const CONSUMABLE_DEFAULT_VISIBLE_COLUMNS: ConsumableColumnId[] = ["name", "brand", "qty", "unitPrice"];

function consumableDefaultColumnVisibility(): Record<ConsumableColumnId, boolean> {
	const visibility = defaultColumnVisibility(CONSUMABLE_TABLE_COLUMNS);
	for (const column of CONSUMABLE_TABLE_COLUMNS) {
		visibility[column.id] = CONSUMABLE_DEFAULT_VISIBLE_COLUMNS.includes(column.id);
	}
	return visibility;
}

function mergeOptionNames(lists: string[][]): string[] {
	const set = new Set<string>();
	for (const list of lists) {
		for (const name of list) {
			const trimmed = name.trim();
			if (trimmed) set.add(trimmed);
		}
	}
	return [...set].sort((a, b) => a.localeCompare(b));
}

function consumableSettingsRow(books: ConsumablesBooks): ConsumableSettingsRow | undefined {
	return books.consumableSettings?.[0];
}

async function saveConsumableSettings(books: ConsumablesBooks, patch: Partial<ConsumableSettingsRow>) {
	const existing = consumableSettingsRow(books);
	const record: ConsumableSettingsRow = {
		categories: existing?.categories ?? [],
		brands: existing?.brands ?? [],
		units: mergeUniqueLists(DEFAULT_CONSUMABLE_UNITS, existing?.units ?? []),
		conversions: existing?.conversions ?? [],
		...patch,
	};
	if (existing?.id != null) {
		await api.updateHubBook({ list: "consumableSettings", id: existing.id, record: { ...existing, ...record } });
		return;
	}
	await api.appendHubBook({ list: "consumableSettings", record });
}

async function ensureConsumableOption(
	books: ConsumablesBooks,
	field: "categories" | "brands",
	name: string,
) {
	const trimmed = name.trim();
	if (!trimmed) return;
	const existing = consumableSettingsRow(books);
	const custom = field === "categories" ? existing?.categories ?? [] : existing?.brands ?? [];
	if (custom.some((entry) => entry.toLowerCase() === trimmed.toLowerCase())) return;
	if (field === "categories" && DEFAULT_CONSUMABLE_CATEGORIES.some((entry) => entry.toLowerCase() === trimmed.toLowerCase())) {
		return;
	}
	const next = [...custom, trimmed].sort((a, b) => a.localeCompare(b));
	await saveConsumableSettings(books, field === "categories" ? { categories: next } : { brands: next });
}

async function removeConsumableOption(
	books: ConsumablesBooks,
	field: "categories" | "brands",
	name: string,
) {
	const trimmed = name.trim();
	if (!trimmed) return;
	const existing = consumableSettingsRow(books);
	if (!existing?.id) return;
	const custom = field === "categories" ? existing.categories ?? [] : existing.brands ?? [];
	const next = custom.filter((entry) => entry.trim().toLowerCase() !== trimmed.toLowerCase());
	await saveConsumableSettings(books, field === "categories" ? { categories: next } : { brands: next });
}

function settingsOf(books: ConsumablesBooks): ConsumableSettings {
	const row = consumableSettingsRow(books);
	return {
		categories: mergeUniqueLists(DEFAULT_CONSUMABLE_CATEGORIES, row?.categories ?? []),
		brands: mergeUniqueLists([], row?.brands ?? []),
		units: mergeUniqueLists(DEFAULT_CONSUMABLE_UNITS, row?.units ?? []),
		conversions: row?.conversions ?? [],
	};
}

function consumableHasIssuedUsage(movements: ConsumableMovementRow[], consumableId: number): boolean {
	return movements.some(
		(row) =>
			row.consumableId === consumableId
			&& row.type === "out"
			&& !row.consumableArchived
			&& (row.issuedUsage || Boolean(row.customerId && row.siteId)),
	);
}

function consumableBalanceCustodySummary(
	balances: ConsumableBalanceRow[],
	consumableId: number | undefined,
): string {
	if (consumableId == null) return "—";
	const onHands = balanceQtyAt(balances, consumableId, normalizeConsumableLocation("internal", "", ""));
	if (onHands <= 0) return "—";
	return `On Hands (${formatQty(onHands)})`;
}

function movementLocationLine(books: ConsumablesBooks, row: ConsumableMovementRow): string {
	if (row.type === "out" && (row.issuedUsage || (row.custody === "internal" && row.customerId && row.siteId))) {
		const customer = customerLabel(books, row.customerId ?? "");
		const site = siteRow(books, row.siteId ?? "")?.name ?? "";
		const target = [customer, site].filter(Boolean).join(" · ") || "Customer site";
		return `Issued from On Hands → ${target}`;
	}
	if (row.type === "transfer") {
		const from = normalizeConsumableLocation(
			row.fromCustody ?? row.fromLocation ?? "internal",
			row.fromCustomerId ?? "",
			row.fromSiteId ?? "",
		);
		const to = normalizeConsumableLocation(
			row.toCustody ?? row.toLocation ?? "internal",
			row.toCustomerId ?? "",
			row.toSiteId ?? "",
		);
		return `${stockLocationLabel(books, from)} → ${stockLocationLabel(books, to)}`;
	}
	const loc = normalizeConsumableLocation(
		row.custody ?? row.fromLocation ?? "internal",
		row.customerId ?? "",
		row.siteId ?? "",
	);
	return stockLocationLabel(books, loc);
}

function consumableMasterRecord(fields: FormData, code: string, qty: string): ConsumableRow {
	return {
		code,
		category: textOf(fields, "category"),
		name: textOf(fields, "name"),
		description: textOf(fields, "description"),
		brand: textOf(fields, "brand"),
		specification: textOf(fields, "specification"),
		unit: textOf(fields, "unit") || "pcs",
		qty,
		minQty: "0",
		unitPrice: textOf(fields, "unitPrice") || "0",
		vendorId: textOf(fields, "vendorId"),
	};
}

function ConsumableFormFields({
	books,
	settings,
	editing,
	onRefresh,
}: {
	books: ConsumablesBooks;
	settings: ConsumableSettings;
	editing?: ConsumableRow | null;
	onRefresh: () => Promise<void>;
}) {
	const vendors = vendorsOf(books);
	const consumableRows = books.consumables ?? [];
	const settingsRow = consumableSettingsRow(books);
	const categories = mergeOptionNames([
		settings.categories,
		consumableRows.map((row) => row.category),
	]);
	const brands = mergeOptionNames([
		settings.brands,
		consumableRows.map((row) => row.brand ?? ""),
	]);
	const removableCategories = settingsRow?.categories ?? [];
	const removableBrands = settingsRow?.brands ?? [];
	return (
		<HubOperationFormStack key={editing?.id ?? "new"}>
			{editing ? (
				<HubOperationFormField label="ID" htmlFor="con-code">
					<input id="con-code" name="code" value={editing.code} readOnly />
				</HubOperationFormField>
			) : (
				<input type="hidden" name="code" value={nextConsumableCode(books.consumables)} />
			)}
			<HubManualLookupFieldSelect
				id="con-category"
				name="category"
				label="Category"
				defaultValue={editing?.category ?? ""}
				items={categories}
				removableItems={removableCategories}
				usageLabel="consumable"
				countUsage={(name) => {
					const needle = name.trim().toLowerCase();
					return consumableRows.filter((row) => row.category.trim().toLowerCase() === needle).length;
				}}
				onRemove={async (name) => {
					await removeConsumableOption(books, "categories", name);
					await onRefresh();
				}}
			/>
			<HubOperationFormField label="Item name" htmlFor="con-name">
				<input id="con-name" name="name" defaultValue={editing?.name ?? ""} placeholder="Cat6 cable" required />
			</HubOperationFormField>
			<HubOperationFormField label="Description" htmlFor="con-description">
				<textarea id="con-description" name="description" rows={2} defaultValue={editing?.description ?? ""} placeholder="Optional" />
			</HubOperationFormField>
			<HubOperationFormRow>
				<HubManualLookupFieldSelect
					id="con-brand"
					name="brand"
					label="Brand"
					defaultValue={editing?.brand ?? ""}
					items={brands}
					removableItems={removableBrands}
					usageLabel="consumable"
					countUsage={(name) => {
						const needle = name.trim().toLowerCase();
						return consumableRows.filter((row) => (row.brand ?? "").trim().toLowerCase() === needle).length;
					}}
					onRemove={async (name) => {
						await removeConsumableOption(books, "brands", name);
						await onRefresh();
					}}
				/>
				<HubOperationFormField label="Specification" htmlFor="con-spec">
					<input id="con-spec" name="specification" defaultValue={editing?.specification ?? ""} />
				</HubOperationFormField>
			</HubOperationFormRow>
			<HubOperationFormField label="Unit" htmlFor="con-unit">
				<Select id="con-unit" name="unit" defaultValue={editing?.unit ?? "pcs"} required>
					{settings.units.map((value) => <option key={value} value={value}>{value}</option>)}
				</Select>
			</HubOperationFormField>
			<HubOperationFormRow>
				{editing ? (
					<HubOperationFormField label="Quantity in stock" htmlFor="con-qty">
						<input id="con-qty" value={editing.qty || "0"} readOnly />
					</HubOperationFormField>
				) : (
					<HubOperationFormField label="Quantity in stock" htmlFor="con-qty-new">
						<input id="con-qty-new" name="qty" type="number" min="0" step="any" defaultValue="0" />
					</HubOperationFormField>
				)}
				<HubOperationFormField label="Unit purchase price" htmlFor="con-price">
					<input id="con-price" name="unitPrice" inputMode="decimal" defaultValue={editing?.unitPrice ?? "0"} />
				</HubOperationFormField>
			</HubOperationFormRow>
			<HubOperationFormField label="Vendor" htmlFor="con-vendor">
				<Select id="con-vendor" name="vendorId" defaultValue={editing?.vendorId ?? ""}>
					<option value="">No vendor</option>
					{vendors.map((row) => (
						<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
					))}
				</Select>
			</HubOperationFormField>
		</HubOperationFormStack>
	);
}

function ConsumableUsageFields({ books }: { books: ConsumablesBooks }) {
	const customers = customersOf(books);
	const [customerId, setCustomerId] = useState("");
	const [siteId, setSiteId] = useState("");
	const sites = sitesForCustomer(books, customerId);
	return (
		<>
			<input type="hidden" name="customerId" value={customerId} />
			<input type="hidden" name="siteId" value={siteId} />
			<p className="quiet m-0">Issued from On Hands for use at this customer site.</p>
			<HubOperationFormRow>
				<HubOperationFormField label="Customer" htmlFor="move-usage-customer">
					<Select
						id="move-usage-customer"
						value={customerId}
						onChange={(event) => {
							setCustomerId(event.target.value);
							setSiteId("");
						}}
						required
					>
						<option value="">Choose customer</option>
						{customers.map((row) => (
							<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
						))}
					</Select>
				</HubOperationFormField>
				<HubOperationFormField label="Site" htmlFor="move-usage-site">
					<Select
						id="move-usage-site"
						value={siteId}
						onChange={(event) => setSiteId(event.target.value)}
						required
					>
						<option value="">Choose site</option>
						{sites.map((row) => (
							<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
						))}
					</Select>
				</HubOperationFormField>
			</HubOperationFormRow>
		</>
	);
}

function MovementFormFields({
	books,
	settings,
	target,
}: {
	books: ConsumablesBooks;
	settings: ConsumableSettings;
	target: ConsumableRow;
}) {
	const [moveType, setMoveType] = useState<"in" | "out">("in");
	const displayQty = useMemo(() => {
		const unit = target.unit || "pcs";
		return `${formatQty(parseQty(target.qty))} ${unit}`;
	}, [target.qty, target.unit]);
	return (
		<HubOperationFormStack key={target.id ?? target.code}>
			<p className="quiet">{target.code} · {target.name} · Total on hand: {displayQty}</p>
			<input type="hidden" name="consumableId" value={String(target.id ?? "")} />
			<input type="hidden" name="type" value={moveType} />
			<HubOperationFormField label="Movement" htmlFor="move-type">
				<Select id="move-type" value={moveType} onChange={(event) => setMoveType(event.target.value === "out" ? "out" : "in")}>
					<option value="in">Stock in</option>
					<option value="out">Stock out</option>
				</Select>
			</HubOperationFormField>
			{moveType === "out" ? (
				<ConsumableUsageFields books={books} />
			) : (
				<p className="quiet m-0">Stock will be added to On Hands.</p>
			)}
			<HubOperationFormRow>
				<HubOperationFormField label="Quantity" htmlFor="move-qty">
					<input id="move-qty" name="qty" type="number" min="0" step="any" required />
				</HubOperationFormField>
				<HubOperationFormField label="Unit" htmlFor="move-unit">
					<Select id="move-unit" name="unit" defaultValue={target.unit || "pcs"}>
						{settings.units.map((value) => <option key={value} value={value}>{value}</option>)}
					</Select>
				</HubOperationFormField>
			</HubOperationFormRow>
			<HubOperationFormField label="Date" htmlFor="move-at">
				<input id="move-at" name="at" type="date" defaultValue={new Date().toISOString().slice(0, 10)} required />
			</HubOperationFormField>
			<HubOperationFormField label="Note" htmlFor="move-note">
				<textarea id="move-note" name="note" rows={2} placeholder="Optional" />
			</HubOperationFormField>
		</HubOperationFormStack>
	);
}

function consumableByRouteId(rows: ConsumableRow[], itemId: string): ConsumableRow | undefined {
	const numeric = Number(itemId);
	if (Number.isFinite(numeric)) {
		return rows.find((row) => row.id === numeric);
	}
	const decoded = decodeURIComponent(itemId);
	return rows.find((row) => row.code === decoded);
}

function ConsumableDetailView({
	books,
	row,
	balances,
	movements,
	onEdit,
	onMove,
	onRemove,
}: {
	books: ConsumablesBooks;
	row: ConsumableRow;
	balances: ConsumableBalanceRow[];
	movements: ConsumableMovementRow[];
	onEdit: () => void;
	onMove: () => void;
	onRemove: () => void;
}) {
	return (
		<HubOperationScreen>
			<HubOperationBar sub={`${row.code} · ${row.name}`}>
				<Link className="btn ghost" to="/hub/consumables">All consumables</Link>
				<button className="btn ghost" type="button" onClick={onEdit}>Edit</button>
				<button className="btn ghost" type="button" onClick={onMove}>Stock movement</button>
				<button className="btn ghost" type="button" onClick={onRemove}>Delete</button>
			</HubOperationBar>
			<div className="panel">
				<ConsumableDetailBody books={books} row={row} balances={balances} movements={movements} />
			</div>
		</HubOperationScreen>
	);
}

function ConsumableDetailBody({
	books,
	row,
	balances,
	movements,
}: {
	books: ConsumablesBooks;
	row: ConsumableRow;
	balances: ConsumableBalanceRow[];
	movements: ConsumableMovementRow[];
}) {
	const onHands = row.id != null
		? balanceQtyAt(balances, row.id, normalizeConsumableLocation("internal", "", ""))
		: 0;
	const usageRows = row.id == null
		? []
		: movements.filter(
			(entry) =>
				entry.consumableId === row.id
				&& entry.type === "out"
				&& !entry.consumableArchived
				&& (entry.issuedUsage || (entry.customerId && entry.siteId)),
		);
	return (
		<div className="stack gap-4">
			<dl className="hub-detail-dl">
				<div><dt>ID</dt><dd>{row.code}</dd></div>
				<div><dt>Category</dt><dd>{row.category || "—"}</dd></div>
				<div><dt>Item</dt><dd>{row.name}</dd></div>
				{row.description ? <div><dt>Description</dt><dd>{row.description}</dd></div> : null}
				<div><dt>Brand</dt><dd>{row.brand || "—"}</dd></div>
				<div><dt>Specification</dt><dd>{row.specification || "—"}</dd></div>
				<div><dt>Unit</dt><dd>{row.unit}</dd></div>
				<div><dt>Available (On Hands)</dt><dd>{formatQty(onHands)} {row.unit}</dd></div>
				<div><dt>Unit price</dt><dd>{formatMyr(row.unitPrice)}</dd></div>
				<div><dt>Vendor</dt><dd>{row.vendorId ? vendorLabel(books, row.vendorId) : "—"}</dd></div>
			</dl>
			<div>
				<h3 className="h6">Site usage history</h3>
				{usageRows.length === 0 ? (
					<p className="quiet m-0">No materials issued to customer sites yet.</p>
				) : (
					<div className="table-wrap">
						<table className="hub-inventory-table">
							<thead>
								<tr>
									<th>Date</th>
									<th>Customer / site</th>
									<th>Issued</th>
									<th>Note</th>
								</tr>
							</thead>
							<tbody>
								{usageRows.map((entry) => (
									<tr key={entry.id ?? `${entry.consumableId}-${entry.at}-${entry.qty}`}>
										<td>{entry.at}</td>
										<td>{movementLocationLine(books, entry).replace(/^Issued from On Hands → /, "")}</td>
										<td>{entry.qty} {entry.unit}</td>
										<td>{entry.note || "—"}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}

function consumableCell(
	balances: ConsumableBalanceRow[],
	row: ConsumableRow,
	columnId: ConsumableColumnId,
): ReactNode {
	switch (columnId) {
		case "code":
			return row.code;
		case "category":
			return row.category || "—";
		case "name":
			return row.name;
		case "brand":
			return row.brand || "—";
		case "qty":
			return `${formatQty(parseQty(row.qty))} ${row.unit}`;
		case "minQty":
			return formatQty(parseQty(row.minQty));
		case "location":
			return consumableBalanceCustodySummary(balances, row.id);
		case "unitPrice":
			return formatMyr(row.unitPrice);
		default:
			return "—";
	}
}

export function ConsumablesHub({ books }: { books: ConsumablesBooks }) {
	const navigate = useNavigate();
	const { docId: itemId } = useParams();
	const client = useQueryClient();
	const refreshBooks = async () => {
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	};
	const searchRef = useRef<HTMLInputElement>(null);
	const rows = books.consumables ?? [];
	const movements = books.consumableMovements ?? [];
	const balances = useMemo(
		() => migrateConsumableBalances(rows, books.consumableBalances ?? []),
		[books.consumableBalances, rows],
	);
	const settings = settingsOf(books);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [view, setView] = useState<ConsumableView>("items");
	const [stockFilter, setStockFilter] = useState<ConsumableStockFilter>("");
	const [groupBy, setGroupBy] = useState<ConsumableGroupBy>("");
	const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
	const [sortKey, setSortKey] = useState<ConsumableColumnId>("code");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
	const [columnVisibility, setColumnVisibility] = useState(() => consumableDefaultColumnVisibility());
	const [editing, setEditing] = useState<ConsumableRow | null>(null);
	const [moving, setMoving] = useState<ConsumableRow | null>(null);
	useHubOperationSearchFocus(searchOpen, searchRef);
	const detail = itemId ? consumableByRouteId(rows, itemId) : undefined;
	const needle = searchQuery.trim().toLowerCase();

	const searchMatched = useMemo(() => {
		if (!needle) return rows;
		return rows.filter((row) => {
			const site = row.siteId ? siteRow(books, row.siteId)?.name ?? "" : "";
			const customer = row.customerId ? customerLabel(books, row.customerId) : "";
			const vendor = row.vendorId ? vendorLabel(books, row.vendorId) : "";
			return [
				row.code,
				row.category,
				row.name,
				row.description,
				row.brand,
				row.specification,
				row.unit,
				customer,
				site,
				vendor,
			].some((value) => (value ?? "").toLowerCase().includes(needle));
		});
	}, [books, needle, rows]);

	const filtered = useMemo(() => {
		let list = searchMatched;
		if (stockFilter === "internal") {
			list = list.filter((row) => row.id != null && consumableHasCustody(balances, row.id, "internal"));
		}
		if (stockFilter === "external") {
			list = list.filter((row) => row.id != null && consumableHasIssuedUsage(movements, row.id));
		}
		return list;
	}, [balances, searchMatched, stockFilter]);

	const sorted = useMemo(() => {
		const copy = [...filtered];
		copy.sort((a, b) => {
			let av = "";
			let bv = "";
			if (sortKey === "location") {
				av = consumableBalanceCustodySummary(balances, a.id);
				bv = consumableBalanceCustodySummary(balances, b.id);
			} else if (sortKey === "qty" || sortKey === "minQty") {
				av = String(parseQty(a[sortKey]));
				bv = String(parseQty(b[sortKey]));
			} else {
				av = String(a[sortKey] ?? "");
				bv = String(b[sortKey] ?? "");
			}
			return compareLocale(av, bv, sortDir);
		});
		return copy;
	}, [balances, books, filtered, sortKey, sortDir]);

	const filteredMovements = useMemo(() => {
		const q = searchQuery.trim().toLowerCase();
		if (!q) return movements;
		return movements.filter((row) => {
			const item = rows.find((entry) => entry.id === row.consumableId);
			const type = row.type === "in" ? "Stock in" : row.type === "out" ? "Stock out" : "Transfer";
			return [row.at, row.note, row.qty, row.unit, type, item?.code, item?.name]
				.some((part) => String(part ?? "").toLowerCase().includes(q));
		});
	}, [movements, rows, searchQuery]);

	const itemsPaginationResetKey = `${searchQuery}|${stockFilter}|${groupBy}|${sortKey}|${sortDir}`;
	const historyPaginationResetKey = `${searchQuery}|history`;
	const itemsPagination = useHubOperationPagination(sorted.length, itemsPaginationResetKey);
	const historyPagination = useHubOperationPagination(filteredMovements.length, historyPaginationResetKey);
	const pagedSorted = useMemo(() => itemsPagination.sliceRows(sorted), [itemsPagination.sliceRows, sorted]);
	const pagedMovements = useMemo(
		() => historyPagination.sliceRows(filteredMovements),
		[filteredMovements, historyPagination.sliceRows],
	);
	const groupedRows = useMemo(() => {
		if (!groupBy) return [["", pagedSorted] as [string, ConsumableRow[]]];
		const map = new Map<string, ConsumableRow[]>();
		for (const row of pagedSorted) {
			const key = row.category.trim() || "General";
			const list = map.get(key) ?? [];
			list.push(row);
			map.set(key, list);
		}
		return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
	}, [pagedSorted, groupBy]);

	const form = useHubSave(async (fields) => {
		const category = textOf(fields, "category");
		const brand = textOf(fields, "brand");
		await Promise.all([
			category ? ensureConsumableOption(books, "categories", category) : Promise.resolve(),
			brand ? ensureConsumableOption(books, "brands", brand) : Promise.resolve(),
		]);
		const code = editing?.code ?? nextConsumableCode(rows);
		const qty = editing?.qty ?? formatQty(parseQty(textOf(fields, "qty") || "0"));
		const record = consumableMasterRecord(fields, code, qty);
		if (editing?.id) {
			await api.updateHubBook({ list: "consumables", id: editing.id, record: { ...editing, ...record } });
		} else {
			const created = await api.appendHubBook({ list: "consumables", record });
			const consumableId = Number(created.id);
			if (Number.isFinite(consumableId) && parseQty(qty) > 0) {
				await api.appendHubBook({
					list: "consumableMovements",
					record: {
						consumableId,
						type: "in",
						qty,
						unit: record.unit,
						at: new Date().toISOString().slice(0, 10),
						note: "Opening quantity",
						custody: "internal",
						customerId: "",
						siteId: "",
					},
				});
			}
		}
		setEditing(null);
		await refreshBooks();
	});

	const movementForm = useHubSave(async (fields) => {
		if (!moving?.id) return;
		const type = normalizeMovementType(textOf(fields, "type"));
		if (type === "transfer") {
			throw new Error("Stock transfer is no longer available. Use stock in or stock out.");
		}
		const qty = textOf(fields, "qty");
		const unit = textOf(fields, "unit") || moving.unit;
		const at = textOf(fields, "at");
		const note = textOf(fields, "note");
		const workingBalances = migrateConsumableBalances(rows, books.consumableBalances ?? []);
		const input =
			type === "out"
				? {
					type,
					qty,
					unit,
					at,
					note,
					usage: {
						customerId: textOf(fields, "customerId"),
						siteId: textOf(fields, "siteId"),
					},
				}
				: {
					type,
					qty,
					unit,
					at,
					note,
				};
		const { movement } = applyConsumableMovement(workingBalances, moving, settings.conversions, input);
		await api.appendHubBook({ list: "consumableMovements", record: movement });
		setMoving(null);
		await refreshBooks();
	});

	async function remove(row: ConsumableRow) {
		if (!row.id || !window.confirm(`Remove ${row.code}?`)) return;
		await api.deleteHubBook({ list: "consumables", id: row.id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		if (itemId === String(row.id)) navigate("/hub/consumables");
	}

	const modals = (
		<>
			<HubOperationModal
				hubForm
				open={form.open}
				title={editing ? "Edit consumable" : "New consumable"}
				error={form.error}
				pending={form.pending}
				onClose={() => { form.setOpen(false); setEditing(null); }}
				onSubmit={(fields) => void form.save(fields)}
			>
				<ConsumableFormFields books={books} settings={settings} editing={editing} onRefresh={refreshBooks} />
			</HubOperationModal>
			<HubOperationModal
				hubForm
				open={movementForm.open}
				title="Stock movement"
				error={movementForm.error}
				pending={movementForm.pending}
				action="Record"
				onClose={() => { movementForm.setOpen(false); setMoving(null); }}
				onSubmit={(fields) => void movementForm.save(fields)}
			>
				{moving ? <MovementFormFields books={books} settings={settings} target={moving} /> : null}
			</HubOperationModal>
		</>
	);

	if (itemId && detail) {
		return (
			<>
				<ConsumableDetailView
					books={books}
					row={detail}
					balances={balances}
					movements={movements}
					onEdit={() => { setEditing(detail); form.setOpen(true); }}
					onMove={() => { setMoving(detail); movementForm.setOpen(true); }}
					onRemove={() => void remove(detail)}
				/>
				{modals}
			</>
		);
	}

	if (itemId && !detail) {
		return (
			<div>
				<HubOperationEmpty>That consumable was not found.</HubOperationEmpty>
				<Link className="btn ghost" to="/hub/consumables">All consumables</Link>
			</div>
		);
	}

	const searchActive = searchQuery.trim().length > 0;
	const listFiltered = Boolean(stockFilter);
	const filteredLabel = searchActive || listFiltered || view === "history";
	const visibleColumns = CONSUMABLE_TABLE_COLUMNS.filter((column) => columnVisibility[column.id]);
	const colSpan = visibleColumns.length + 1;

	const internalCount = searchMatched.filter((row) => row.id != null && consumableHasCustody(balances, row.id, "internal")).length;
	const externalCount = searchMatched.filter((row) => row.id != null && consumableHasIssuedUsage(movements, row.id)).length;

	function toggleSort(key: ConsumableColumnId) {
		const next = toggleHubSort(sortKey, sortDir, key);
		setSortKey(next.sortKey as ConsumableColumnId);
		setSortDir(next.sortDir);
	}

	function clearFilters() {
		setStockFilter("");
	}

	function renderConsumableRow(row: ConsumableRow) {
		return (
			<tr key={row.id ?? row.code}>
				{visibleColumns.map((column) => (
					<td key={column.id} data-label={column.label}>
						{consumableCell(balances, row, column.id)}
					</td>
				))}
				<td className="tight" onClick={(event) => event.stopPropagation()}>
					{row.id ? (
						<details className="select menu">
							<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
							<div className="select-pop">
								<Link className="select-item" to={`/hub/consumables/${row.id}`}>View</Link>
								<button className="select-item" type="button" onClick={() => { setEditing(row); form.setOpen(true); }}>Edit</button>
								<button className="select-item" type="button" onClick={() => { setMoving(row); movementForm.setOpen(true); }}>Stock movement</button>
								<button className="select-item" type="button" onClick={() => void remove(row)}>Delete</button>
							</div>
						</details>
					) : null}
				</td>
			</tr>
		);
	}

	return (
		<HubOperationScreen>
			<HubOperationBar sub={`${view === "history" ? filteredMovements.length : sorted.length} ${view === "history" ? (filteredMovements.length === 1 ? "movement" : "movements") : (sorted.length === 1 ? "consumable" : "consumables")}${filteredLabel ? " · filtered" : ""}`}>
				<HubOperationToolbar
					searchSlot={(
						<HubOperationSearchField
							id="hub-consumables-search"
							ariaLabel={view === "history" ? "Search stock history" : "Search consumables"}
							placeholder={view === "history" ? "Search history…" : "Search consumables…"}
							searchOpen={searchOpen}
							searchQuery={searchQuery}
							searchRef={searchRef}
							onQueryChange={setSearchQuery}
							onEscape={() => { setSearchQuery(""); setSearchOpen(false); }}
						/>
					)}
					tools={(
						<>
							<HubOperationSearchToggle
								searchOpen={searchOpen}
								searchActive={searchActive}
								ariaLabel={view === "history" ? "Search stock history" : "Search consumables"}
								onToggle={() => {
									if (searchOpen) setSearchQuery("");
									setSearchOpen(!searchOpen);
								}}
							/>
							{view === "items" ? (
								<HubOperationColumnMenu
									columns={CONSUMABLE_TABLE_COLUMNS}
									visibility={columnVisibility}
									onToggle={(id, checked) => setColumnVisibility((current) => ({ ...current, [id]: checked }))}
								/>
							) : null}
						</>
					)}
					primaryAction={(
						<button className="btn" type="button" onClick={() => { setEditing(null); form.setOpen(true); }}>New consumable</button>
					)}
				/>
			</HubOperationBar>
			<div className="task-filters">
				<HubOperationFilterMenu
					label="View"
					ariaLabel="Consumables view"
					value={view}
					onPick={(next) => setView(next === "history" ? "history" : "items")}
					active={view === "history"}
					items={[
						["items", "Items", rows.length],
						["history", "History", movements.length],
					]}
				/>
				{view === "items" ? (
					<HubOperationFilterMenu
						label="Stock"
						ariaLabel="Stock filter"
						value={stockFilter}
						onPick={(next) => setStockFilter(next as ConsumableStockFilter)}
						active={Boolean(stockFilter)}
						counted
						items={[
							["", "All", searchMatched.length],
							["internal", "On Hands", internalCount],
							["external", "Site usage", externalCount],
						]}
					/>
				) : null}
				{view === "items" ? (
					<HubOperationFilterMenu
						label="Group"
						ariaLabel="Group list"
						value={groupBy}
						onPick={(next) => {
							setGroupBy(next as ConsumableGroupBy);
							setCollapsedGroups([]);
						}}
						active={false}
						items={[
							["", "All"],
							["category", "Category"],
						]}
					/>
				) : null}
				{view === "items" && listFiltered ? <HubOperationFilterClear onClear={clearFilters} /> : null}
			</div>
			{view === "history" ? (
				<HubOperationListTable>
					<div className="table-wrap hub-inventory-table-wrap">
						<table className="hub-inventory-table">
							<thead>
								<tr>
									<th>Date</th>
									<th>Item</th>
									<th>Type</th>
									<th>Location</th>
									<th>Qty</th>
									<th>Note</th>
								</tr>
							</thead>
							<tbody>
								{filteredMovements.length === 0 ? (
									<HubOperationTableEmptyRow colSpan={6}>
										{movements.length === 0 ? "No stock movements yet." : "No movements match your search."}
									</HubOperationTableEmptyRow>
								) : pagedMovements.map((row) => {
									const item = rows.find((entry) => entry.id === row.consumableId);
									return (
										<tr key={row.id ?? `${row.consumableId}-${row.at}-${row.type}`}>
											<td data-label="Date">{row.at}</td>
											<td data-label="Item">{item ? `${item.code} · ${item.name}` : `#${row.consumableId}`}</td>
											<td data-label="Type">{row.type === "in" ? "Stock in" : row.type === "out" ? "Stock out" : "Transfer"}</td>
											<td data-label="Location">{movementLocationLine(books, row)}</td>
											<td data-label="Qty">{row.qty} {row.unit}</td>
											<td data-label="Note">{row.note || "—"}</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
					<HubOperationTablePagination
						total={filteredMovements.length}
						page={historyPagination.page}
						pageSize={historyPagination.pageSize}
						totalPages={historyPagination.totalPages}
						onPage={historyPagination.setPage}
						onPageSize={historyPagination.setPageSize}
						totalNoun="movements"
					/>
				</HubOperationListTable>
			) : (
				<HubOperationListTable>
				<div className="table-wrap hub-inventory-table-wrap">
					<table className="hub-inventory-table">
						<thead>
							<tr>
								{visibleColumns.map((column) => (
									<th key={column.id}>
										{column.sortable ? (
											<button type="button" className="th-sort plain" onClick={() => toggleSort(column.id)}>
												{column.label}
												{hubTableSortIndicator(sortKey, column.id, sortDir)}
											</button>
										) : column.label}
									</th>
								))}
								<th className="tight"></th>
							</tr>
						</thead>
						<tbody>
							{!hubOperationGroupedHasRows(groupedRows) ? (
								<HubOperationTableEmptyRow colSpan={colSpan}>
									{filteredLabel || rows.length > 0 ? "No consumables match your filters." : "No consumables yet."}
								</HubOperationTableEmptyRow>
							) : groupedRows.flatMap(([groupLabel, groupItems]) => {
								const collapsed = collapsedGroups.includes(groupLabel);
								const header = groupBy ? (
									<HubOperationGroupHeader
										key={`grp-${groupLabel}`}
										label={groupLabel || "General"}
										collapsed={collapsed}
										colSpan={colSpan}
										onToggle={() => setCollapsedGroups((current) => (
											current.includes(groupLabel) ? current.filter((c) => c !== groupLabel) : [...current, groupLabel]
										))}
									/>
								) : null;
								return [
									...(header ? [header] : []),
									...(groupBy && collapsed ? [] : groupItems.map((row) => renderConsumableRow(row))),
								];
							})}
						</tbody>
					</table>
				</div>
				<HubOperationTablePagination
					total={sorted.length}
					page={itemsPagination.page}
					pageSize={itemsPagination.pageSize}
					totalPages={itemsPagination.totalPages}
					onPage={itemsPagination.setPage}
					onPageSize={itemsPagination.setPageSize}
					totalNoun="consumables"
				/>
				</HubOperationListTable>
			)}
			{modals}
		</HubOperationScreen>
	);
}
